import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { doc, updateDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../../firebase/config';
import {
  User, Mail, Phone, MapPin, Building, Shield, Save, FileText,
  Upload, CheckCircle, AlertCircle, ArrowLeft, Info, Eye, Trash2, Download
} from 'lucide-react';
import { toast } from 'react-toastify';

const BuyerProfile = () => {
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    companyName: '',
    businessType: '',
    address: '',
    country: '',
    state: '',
    city: '',
    postalCode: '',
    website: '',
    GSTNumber: '',
    licenseNumber: ''
  });

  const [profileImage, setProfileImage] = useState(null);
  const [profileImageUrl, setProfileImageUrl] = useState('');
  const [profilePercentage, setProfilePercentage] = useState(0);

  const countries = [
    { value: 'IN', label: 'India' },
    { value: 'US', label: 'United States' },
    { value: 'GB', label: 'United Kingdom' },
    { value: 'AE', label: 'United Arab Emirates' },
    { value: 'SG', label: 'Singapore' },
    { value: 'CN', label: 'China' },
    { value: 'DE', label: 'Germany' },
    { value: 'FR', label: 'France' },
    { value: 'JP', label: 'Japan' }
  ];

  const businessTypes = [
    'Manufacturer',
    'Retailer',
    'Wholesaler',
    'Distributor',
    'Importer',
    'E-commerce',
    'Service Provider',
    'Government',
    'Non-Profit',
    'Individual'
  ];

  // Calculate profile completion percentage
  const calculateProfilePercentage = (data) => {
    const requiredFields = [
      'name', 'email', 'phone', 'companyName',
      'address', 'country', 'city', 'state', 'postalCode'
    ];
    
    const filledFields = requiredFields.filter(field => 
      data[field] && data[field].toString().trim() !== ''
    ).length;
    
    const percentage = Math.round((filledFields / requiredFields.length) * 100);
    return Math.min(percentage, 100);
  };

  // Load user data
  useEffect(() => {
    const loadUserData = async () => {
      if (user?.uid) {
        try {
          setLoading(true);
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const data = userDoc.data();
            const loadedFormData = {
              name: data.name || '',
              email: data.email || '',
              phone: data.phone || '',
              companyName: data.companyName || '',
              businessType: data.businessType || '',
              address: data.address || '',
              country: data.country || '',
              state: data.state || '',
              city: data.city || '',
              postalCode: data.postalCode || '',
              website: data.website || '',
              GSTNumber: data.GSTNumber || '',
              licenseNumber: data.licenseNumber || ''
            };
            
            setFormData(loadedFormData);

            // Set profile image URL if exists
            if (data.profileImageUrl) setProfileImageUrl(data.profileImageUrl);

            // Calculate profile percentage
            const percentage = calculateProfilePercentage(loadedFormData);
            setProfilePercentage(percentage);
          }
        } catch (error) {
          console.error('Error loading user data:', error);
          toast.error('Failed to load profile data');
        } finally {
          setLoading(false);
        }
      }
    };

    loadUserData();
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileUpload = async (file, type) => {
    if (!file || !user?.uid) return null;
    
    try {
      setUploading(true);
      const fileRef = ref(storage, `users/${user.uid}/${type}/${Date.now()}_${file.name}`);
      await uploadBytes(fileRef, file);
      const downloadURL = await getDownloadURL(fileRef);
      setUploading(false);
      return downloadURL;
    } catch (error) {
      setUploading(false);
      console.error('File upload error:', error);
      toast.error(`Failed to upload ${type}`);
      return null;
    }
  };

  const handleRemoveFile = async (type) => {
    try {
      if (type === 'profile' && profileImageUrl) {
        const fileRef = ref(storage, profileImageUrl);
        await deleteObject(fileRef);
        setProfileImageUrl('');
        setProfileImage(null);
        toast.success('Profile image removed');
      }
    } catch (error) {
      console.error('File removal error:', error);
      // Even if deletion fails, clear from state
      if (type === 'profile') {
        setProfileImageUrl('');
        setProfileImage(null);
      }
      toast.success('File removed from profile');
    }
  };

  const handleFileSelect = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const maxSize = 2 * 1024 * 1024; // 2MB
    
    if (!validImageTypes.includes(file.type)) {
      toast.error('Please upload a valid image (JPG, PNG, WEBP)');
      return;
    }
    
    if (file.size > maxSize) {
      toast.error(`File size should be less than ${maxSize / 1024 / 1024}MB`);
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    setProfileImage(file);
    setProfileImageUrl(previewUrl);
    e.target.value = null;
  };

  const viewFile = (type) => {
    const url = profileImageUrl;
    if (url) {
      window.open(url, '_blank');
    }
  };

  const validateForm = () => {
    const errors = [];
    
    const requiredFields = [
      { field: 'name', label: 'Full Name' },
      { field: 'phone', label: 'Phone Number' },
      { field: 'companyName', label: 'Company Name' },
      { field: 'address', label: 'Address' },
      { field: 'country', label: 'Country' },
      { field: 'state', label: 'State/Province' },
      { field: 'city', label: 'City' },
      { field: 'postalCode', label: 'Postal Code' }
    ];

    requiredFields.forEach(({ field, label }) => {
      if (!formData[field]?.toString().trim()) {
        errors.push(`${label} is required`);
      }
    });

    // Validate phone format (Indian phone number)
    if (formData.phone && !/^[\+]?[1-9][\d]{9,14}$/.test(formData.phone.replace(/[\s\-\(\)]/g, ''))) {
      errors.push('Please enter a valid phone number');
    }

    if (errors.length > 0) {
      toast.error(errors[0]);
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSaving(true);

    try {
      let profileImageUrlToSave = profileImageUrl;

      // Upload new profile image if selected
      if (profileImage) {
        const uploadedUrl = await handleFileUpload(profileImage, 'profileImage');
        if (uploadedUrl) {
          profileImageUrlToSave = uploadedUrl;
          if (profileImageUrl && profileImageUrl.startsWith('blob:')) {
            URL.revokeObjectURL(profileImageUrl);
          }
          setProfileImageUrl(uploadedUrl);
        }
      }

      // Calculate profile percentage
      const newProfilePercentage = calculateProfilePercentage(formData);

      // Prepare update data - only essential fields
      const updateData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        companyName: formData.companyName,
        businessType: formData.businessType,
        address: formData.address,
        country: formData.country,
        state: formData.state,
        city: formData.city,
        postalCode: formData.postalCode,
        website: formData.website || '',
        GSTNumber: formData.GSTNumber || '',
        licenseNumber: formData.licenseNumber || '',
        currency: 'INR', // Set currency to INR
        updatedAt: serverTimestamp(),
        profilePercentage: newProfilePercentage,
        userType: 'buyer'
      };

      // Add profile image if exists
      if (profileImageUrlToSave) {
        updateData.profileImageUrl = profileImageUrlToSave;
      }

      // Update Firestore
      await updateDoc(doc(db, 'users', user.uid), updateData);

      // Clear file input
      setProfileImage(null);

      // Update local state
      setProfilePercentage(newProfilePercentage);
      setIsEditing(false);
      
      toast.success('Profile updated successfully!');

    } catch (error) {
      console.error('Profile update error:', error);
      toast.error('Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    // Reload original data
    if (user?.uid) {
      getDoc(doc(db, 'users', user.uid)).then(doc => {
        if (doc.exists()) {
          const data = doc.data();
          setFormData({
            name: data.name || '',
            email: data.email || '',
            phone: data.phone || '',
            companyName: data.companyName || '',
            businessType: data.businessType || '',
            address: data.address || '',
            country: data.country || '',
            state: data.state || '',
            city: data.city || '',
            postalCode: data.postalCode || '',
            website: data.website || '',
            GSTNumber: data.GSTNumber || '',
            licenseNumber: data.licenseNumber || ''
          });
          
          if (data.profileImageUrl) setProfileImageUrl(data.profileImageUrl);
          
          const percentage = calculateProfilePercentage(data);
          setProfilePercentage(percentage);
        }
      });
    }
  };

  const handleBack = () => {
    window.history.back();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white p-10 sm:p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="h-12 w-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white p-10 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <button
            onClick={handleBack}
            className="flex items-center text-blue-600 hover:text-blue-700 mb-4 text-sm sm:text-base"
          >
            <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
            Back to Dashboard
          </button>
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-xl sm:text-2xl font-semibold text-gray-800">Buyer Profile</h1>
              <p className="text-gray-600 mt-1">Manage your account information</p>
            </div>
            {!isEditing && (
              <button
                onClick={() => setIsEditing(true)}
                className="px-4 sm:px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md transition-colors flex items-center"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit Profile
              </button>
            )}
          </div>
        </div>

        {/* Profile Completion */}
        <div className="bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="font-semibold text-gray-800">Profile Completion</h3>
              <p className="text-sm text-gray-600 mt-1">
                Complete your profile for better business opportunities
              </p>
            </div>
            <div className="text-right">
              <span className="text-2xl font-bold text-blue-600">{profilePercentage}%</span>
              <p className="text-xs text-gray-600 mt-1">Complete</p>
            </div>
          </div>
          <div className="mt-3 w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${profilePercentage}%` }}
            ></div>
          </div>
        </div>

        {/* Main Form */}
        <form onSubmit={handleSubmit}>
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            {isEditing ? (
              <div className="border-b border-gray-200 px-6 py-4 bg-gray-50">
                <h2 className="text-lg font-semibold text-gray-800">Editing Mode</h2>
                <p className="text-sm text-gray-600">Update your information below</p>
              </div>
            ) : null}

            <div className="p-6">
              {/* Personal & Company Information */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <User className="h-5 w-5 mr-2 text-blue-500" />
                  Personal & Company Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-6">
                  {/* Full Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <span className="text-red-500">*</span> Full Name
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-md text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base disabled:bg-gray-50"
                        placeholder="Enter your full name"
                        required
                      />
                      <User className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <div className="relative">
                      <input
                        type="email"
                        value={formData.email}
                        disabled
                        className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-md text-gray-700 bg-gray-50 text-sm sm:text-base"
                      />
                      <Mail className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
                    </div>
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <span className="text-red-500">*</span> Phone Number
                    </label>
                    <div className="relative">
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-md text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base disabled:bg-gray-50"
                        placeholder="+91 9876543210"
                        required
                      />
                      <Phone className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
                    </div>
                  </div>

                  {/* Company Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <span className="text-red-500">*</span> Company Name
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-md text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base disabled:bg-gray-50"
                        placeholder="Enter company name"
                        required
                      />
                      <Building className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
                    </div>
                  </div>

                  {/* Business Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Business Type
                    </label>
                    <div className="relative">
                      <select
                        name="businessType"
                        value={formData.businessType}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-md text-gray-700 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 appearance-none text-sm sm:text-base disabled:bg-gray-50"
                      >
                        <option value="">Select Business Type</option>
                        {businessTypes.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 sm:px-3 text-gray-500">
                        <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                          <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Website */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Website
                    </label>
                    <div className="relative">
                      <input
                        type="url"
                        name="website"
                        value={formData.website}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-md text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base disabled:bg-gray-50"
                        placeholder="https://example.com"
                      />
                    </div>
                  </div>

                  {/* GST Number */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      GST Number
                    </label>
                    <input
                      type="text"
                      name="GSTNumber"
                      value={formData.GSTNumber}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-md text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base disabled:bg-gray-50"
                      placeholder="Enter GST number"
                    />
                  </div>

                  {/* License Number */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Business License Number
                    </label>
                    <input
                      type="text"
                      name="licenseNumber"
                      value={formData.licenseNumber}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-md text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base disabled:bg-gray-50"
                      placeholder="Enter license number"
                    />
                  </div>
                </div>
              </div>

              {/* Address Information */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <MapPin className="h-5 w-5 mr-2 text-blue-500" />
                  Address Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Country */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <span className="text-red-500">*</span> Country
                    </label>
                    <div className="relative">
                      <select
                        name="country"
                        value={formData.country}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:bg-gray-50"
                        required
                      >
                        <option value="">Select Country</option>
                        {countries.map(country => (
                          <option key={country.value} value={country.value}>
                            {country.label}
                          </option>
                        ))}
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-500">
                        <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                          <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* State/Province */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <span className="text-red-500">*</span> State/Province
                    </label>
                    <input
                      type="text"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:bg-gray-50"
                      placeholder="e.g., Maharashtra"
                      required
                    />
                  </div>

                  {/* City */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <span className="text-red-500">*</span> City
                    </label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:bg-gray-50"
                      placeholder="e.g., Mumbai"
                      required
                    />
                  </div>

                  {/* Postal Code */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <span className="text-red-500">*</span> Postal Code
                    </label>
                    <input
                      type="text"
                      name="postalCode"
                      value={formData.postalCode}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:bg-gray-50"
                      placeholder="e.g., 400001"
                      required
                    />
                  </div>

                  {/* Full Address */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <span className="text-red-500">*</span> Full Address
                    </label>
                    <textarea
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      rows="3"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:bg-gray-50"
                      placeholder="Street address, building, floor, etc."
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Profile Image Upload */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Profile Image</h3>
                
                <div className="flex flex-col lg:flex-row gap-6 sm:gap-8">
                  {/* Left side - Image placeholder */}
                  <div className="flex justify-center sm:justify-start">
                    <div className="w-full sm:w-auto">
                      <div className="relative">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Profile Picture
                        </label>
                        <div 
                          className={`w-full sm:w-48 h-48 rounded-lg flex flex-col items-center justify-center cursor-pointer transition-all ${
                            profileImageUrl 
                              ? 'border-2 border-gray-300 bg-gray-50 overflow-hidden' 
                              : 'border-2 border-dashed border-gray-300 bg-gray-50 hover:border-blue-400'
                          }`}
                          onClick={() => profileImageUrl && viewFile('profile')}
                        >
                          {profileImageUrl ? (
                            <>
                              <img
                                src={profileImageUrl}
                                alt="Profile"
                                className="w-full h-full object-cover rounded-lg"
                                onError={(e) => {
                                  e.target.src = 'https://via.placeholder.com/400x400?text=Profile+Image';
                                }}
                              />
                              <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-10 transition-all rounded-lg" />
                            </>
                          ) : (
                            <>
                              <div className="flex flex-col items-center p-4">
                                <User className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400 mb-2" />
                                <span className="text-gray-500 text-sm text-center">Profile Image</span>
                                <span className="text-gray-400 text-xs mt-1 text-center">Click to upload</span>
                              </div>
                            </>
                          )}
                        </div>
                        
                        {/* Action buttons below */}
                        <div className="flex gap-2 mt-3 justify-center flex-wrap">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              document.getElementById('file-profile').click();
                            }}
                            className={`p-2 transition-colors rounded-md ${
                              profileImageUrl
                                ? 'text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100'
                                : 'text-gray-600 hover:text-blue-600 bg-gray-100 hover:bg-gray-200'
                            }`}
                            title={profileImageUrl ? "Replace Image" : "Upload Image"}
                            disabled={!isEditing || uploading}
                          >
                            {profileImageUrl ? (
                              <Upload className="w-4 h-4 sm:w-5 sm:h-5" />
                            ) : (
                              <Download className="w-4 h-4 sm:w-5 sm:h-5" />
                            )}
                          </button>
                          
                          {profileImageUrl && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                viewFile('profile');
                              }}
                              className="p-2 text-gray-600 hover:text-green-600 transition-colors bg-gray-100 hover:bg-gray-200 rounded-md"
                              title="View Image"
                              disabled={!isEditing}
                            >
                              <Eye className="w-4 h-4 sm:w-5 sm:h-5" />
                            </button>
                          )}
                          
                          {profileImageUrl && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (window.confirm('Are you sure you want to remove profile image?')) {
                                  handleRemoveFile('profile');
                                }
                              }}
                              className="p-2 text-gray-600 hover:text-red-600 transition-colors bg-gray-100 hover:bg-gray-200 rounded-md"
                              title="Remove Image"
                              disabled={!isEditing || uploading}
                            >
                              <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
                            </button>
                          )}
                          
                          <input
                            id="file-profile"
                            type="file"
                            accept="image/jpeg,image/jpg,image/png,image/webp"
                            onChange={(e) => handleFileSelect(e, 'profile')}
                            className="hidden"
                            disabled={!isEditing || uploading}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right side - Tips */}
                  <div className="flex-1">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-start">
                        <Info className="w-5 h-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
                        <div>
                          <h3 className="text-sm font-semibold text-blue-900 mb-3">Profile Image Guidelines</h3>
                          <ul className="text-sm text-gray-700 space-y-2">
                            <li className="flex items-start">
                              <span className="text-gray-400 mr-2">•</span>
                              <span>Upload a clear, professional headshot or company logo</span>
                            </li>
                            <li className="flex items-start">
                              <span className="text-gray-400 mr-2">•</span>
                              <span>Supported formats: JPG, PNG, WebP</span>
                            </li>
                            <li className="flex items-start">
                              <span className="text-gray-400 mr-2">•</span>
                              <span>Maximum file size: 2MB</span>
                            </li>
                            <li className="flex items-start">
                              <span className="text-gray-400 mr-2">•</span>
                              <span>Recommended dimensions: 400x400 pixels</span>
                            </li>
                            <li className="flex items-start">
                              <span className="text-gray-400 mr-2">•</span>
                              <span>A profile image helps build trust with sellers</span>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              {isEditing && (
                <div className="flex flex-col sm:flex-row justify-end gap-4 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="px-6 sm:px-8 py-2.5 border border-gray-300 bg-white text-gray-700 font-medium rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 order-2 sm:order-1"
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 sm:px-8 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[100px] order-1 sm:order-2"
                    disabled={saving || uploading}
                  >
                    {saving ? (
                      <>
                        <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        Saving...
                      </>
                    ) : 'Save Changes'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </form>

        {/* Account Status */}
        <div className="mt-6 bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Account Status</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-center">
              <div className={`h-12 w-12 rounded-full flex items-center justify-center mr-4 ${user?.isVerified ? 'bg-green-100' : 'bg-yellow-100'}`}>
                {user?.isVerified ? (
                  <CheckCircle className="h-6 w-6 text-green-600" />
                ) : (
                  <AlertCircle className="h-6 w-6 text-yellow-600" />
                )}
              </div>
              <div>
                <p className="font-medium text-gray-900">Verification</p>
                <p className="text-sm text-gray-600">
                  {user?.isVerified ? 'Verified Account' : 'Verification Pending'}
                </p>
              </div>
            </div>
            
            <div className="flex items-center">
              <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center mr-4">
                <Shield className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">Account Type</p>
                <p className="text-sm text-gray-600">Buyer</p>
              </div>
            </div>
          </div>
          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-700">
              <span className="font-medium">Currency:</span> INR (₹)
            </p>
            <p className="text-sm text-gray-700 mt-1">
              <span className="font-medium">Member Since:</span> {user?.createdAt ? new Date(user.createdAt.seconds * 1000).toLocaleDateString() : 'N/A'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BuyerProfile;